// Persistent across every page (rendered once in App.jsx's AppShell, not
// per-page) so feedback is always one click away regardless of where in the
// app someone is. One-way: submits to the feedback table via
// src/lib/db.js's submitFeedback, nothing reads it back here -- submissions
// are triaged by the /feedback-review skill (.claude/skills/feedback-review).
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { COLORS, FONT_SIZE, LAYOUT, RADIUS, SPACE } from "../lib/constants";
import { submitFeedback } from "../lib/db";
import { useIsMobile } from "../hooks/useIsMobile";

export function FeedbackButton({ householdId, email }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const location = useLocation();
  const isMobile = useIsMobile();
  const { card, ink, soft, accent, danger, line } = COLORS;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!message.trim() || status === "sending") return;
    setStatus("sending");
    try {
      await submitFeedback(householdId, email, message.trim(), location.pathname);
      setStatus("sent");
      setMessage("");
      setTimeout(() => {
        setStatus("idle");
        setOpen(false);
      }, 2000);
    } catch {
      setStatus("error");
    }
  }

  return (
    // On a phone it sits above the bottom tab bar (see AppNav.tsx's TabBar).
    <div style={{ position: "fixed", bottom: isMobile ? `calc(${LAYOUT.tabBarHeight}px + env(safe-area-inset-bottom) + var(--space-xl))` : SPACE.x3, right: isMobile ? SPACE.xl : SPACE.x3, zIndex: 100 }}>
      {open && (
        <div
          className="mp-sans"
          style={{
            position: "absolute",
            bottom: "calc(100% + var(--space-lg))",
            right: 0,
            width: 280,
            background: card,
            border: `1px solid ${line}`,
            borderRadius: RADIUS.xl,
            padding: SPACE.xl2,
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
          }}
        >
          {status === "sent" ? (
            <p style={{ margin: 0, fontSize: FONT_SIZE.base, color: accent, fontWeight: 600 }}>Thanks for the feedback!</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <label style={{ fontSize: FONT_SIZE.sm, color: soft, display: "block", marginBottom: SPACE.sm }}>Have feedback or found a bug?</label>
              <textarea
                autoFocus
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what's on your mind…"
                rows={4}
                style={{
                  width: "100%",
                  padding: "var(--space-md) var(--space-lg)",
                  borderRadius: RADIUS.md,
                  border: `1px solid ${line}`,
                  background: COLORS.card,
                  fontSize: FONT_SIZE.base,
                  color: ink,
                  fontFamily: "var(--font-sans)",
                  resize: "vertical",
                  boxSizing: "border-box",
                  marginBottom: SPACE.md,
                }}
              />
              <div style={{ display: "flex", gap: SPACE.md, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="mp-btn"
                  style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!message.trim() || status === "sending"}
                  className="mp-btn"
                  style={{
                    background: accent,
                    color: COLORS.onAccent,
                    padding: "var(--space-sm) var(--space-xl)",
                    borderRadius: RADIUS.md,
                    fontSize: FONT_SIZE.base,
                    fontWeight: 500,
                    opacity: !message.trim() || status === "sending" ? 0.6 : 1,
                  }}
                >
                  {status === "sending" ? "Sending…" : "Send"}
                </button>
              </div>
              {status === "error" && <p style={{ color: danger, fontSize: FONT_SIZE.sm, margin: "var(--space-md) 0 0" }}>Something went wrong — try again.</p>}
            </form>
          )}
        </div>
      )}
      <button
        className="mp-btn mp-sans"
        onClick={() => setOpen((o) => !o)}
        style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-lg) var(--space-xxl)", borderRadius: RADIUS.pill, fontSize: FONT_SIZE.base, fontWeight: 500, boxShadow: "0 4px 12px rgba(0,0,0,0.2)", border: "none" }}
      >
        {open ? "Close" : "💬 Feedback"}
      </button>
    </div>
  );
}
